import React from 'react';
import { FileText, Target, Zap, Shield, Users, TrendingUp, Globe, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Whitepaper = () => {
  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
            <FileText className="text-brand-primary" size={36} />
          </div>
          <h1 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>MatrixLaunch Whitepaper</h1>
          <p className="body-medium mb-6" style={{ color: 'var(--text-muted)' }}>Technical Overview & Vision</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Version 1.0 | December 2025</p>
        </div>

        {/* Executive Summary */}
        <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>Executive Summary</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              MatrixLaunch is a decentralized multi-chain launchpad designed to democratize access to early-stage blockchain investments while fostering sustainable community growth through an innovative multi-level referral system. By combining rigorous project vetting, blockchain transparency, and network effects, we create a trustworthy ecosystem where investors and projects thrive together.
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              Our platform addresses critical pain points in the crypto fundraising landscape: lack of investor protection, information asymmetry, and unsustainable token distribution models. Through smart contract automation and community-driven governance, MatrixLaunch sets a new standard for fairness and efficiency in token launches.
            </p>
          </CardContent>
        </Card>

        {/* Problem Statement */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>1. Problem Statement</h2>
            
            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>1.1 Current Market Challenges</h3>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• <strong>Scam Prevalence:</strong> 80% of new token projects are rugpulls or scams</li>
              <li>• <strong>Barrier to Entry:</strong> Early-stage investments dominated by VCs and insiders</li>
              <li>• <strong>Lack of Transparency:</strong> Opaque tokenomics and team backgrounds</li>
              <li>• <strong>Poor Distribution:</strong> Whale dominance leading to market manipulation</li>
              <li>• <strong>Regulatory Uncertainty:</strong> Compliance gaps exposing investors to legal risk</li>
            </ul>

            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>1.2 Investor Pain Points</h3>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li>• Difficulty identifying legitimate projects</li>
              <li>• High gas fees on Ethereum limiting participation</li>
              <li>• Lack of post-investment support and tracking</li>
              <li>• No recourse for failed projects</li>
            </ul>
          </CardContent>
        </Card>

        {/* Solution */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>2. MatrixLaunch Solution</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="text-brand-primary" size={24} />
                  <h3 className="heading-2" style={{ color: 'var(--text-primary)' }}>Project Vetting</h3>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Multi-stage due diligence process including team KYC, smart contract audits by CertiK/Hacken, tokenomics review, and legal compliance verification. Only 5-10% of applications approved.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="text-brand-primary" size={24} />
                  <h3 className="heading-2" style={{ color: 'var(--text-primary)' }}>Multi-Chain Support</h3>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Deploy on Ethereum, BSC, Polygon, Avalanche, and emerging L2s. Lower gas fees increase accessibility for retail investors globally.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Users className="text-brand-primary" size={24} />
                  <h3 className="heading-2" style={{ color: 'var(--text-primary)' }}>Network Effects</h3>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  5-level referral system incentivizes organic community building. Unlike traditional MLM, commissions paid from platform revenue, not user investments.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Award className="text-brand-primary" size={24} />
                  <h3 className="heading-2" style={{ color: 'var(--text-primary)' }}>Fair Distribution</h3>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Anti-whale mechanisms (max allocation caps), lottery system for oversubscribed sales, tiered allocation based on platform activity and token holdings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technology Stack */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>3. Technology Architecture</h2>
            
            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>3.1 Smart Contract Layer</h3>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Built on Solidity 0.8.x with OpenZeppelin security libraries. Contracts handle:
            </p>
            <ul className="space-y-2 mb-6 ml-4" style={{ color: 'var(--text-secondary)' }}>
              <li>• Token sale mechanics (fixed price, Dutch auction, batch auction)</li>
              <li>• Vesting schedules with cliff periods</li>
              <li>• Referral commission distribution</li>
              <li>• Emergency pause and fund recovery</li>
              <li>• Governance voting for platform decisions</li>
            </ul>

            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>3.2 Backend Infrastructure</h3>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Scalable microservices architecture on AWS/GCP:
            </p>
            <ul className="space-y-2 mb-6 ml-4" style={{ color: 'var(--text-secondary)' }}>
              <li>• Node.js API servers with load balancing</li>
              <li>• MongoDB for user data and analytics</li>
              <li>• Redis caching for real-time updates</li>
              <li>• IPFS for decentralized file storage</li>
              <li>• The Graph for blockchain indexing</li>
            </ul>

            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>3.3 Security Measures</h3>
            <ul className="space-y-2 ml-4" style={{ color: 'var(--text-secondary)' }}>
              <li>• Multi-signature wallets for treasury management</li>
              <li>• Rate limiting and DDoS protection</li>
              <li>• Regular penetration testing by third parties</li>
              <li>• Bug bounty program (up to $100K rewards)</li>
              <li>• Insurance fund for platform vulnerabilities</li>
            </ul>
          </CardContent>
        </Card>

        {/* Tokenomics */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>4. Platform Tokenomics</h2>
            
            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>4.1 MTRX Token Utility</h3>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• <strong>Staking:</strong> Stake MTRX to access guaranteed allocations</li>
              <li>• <strong>Fee Discounts:</strong> Pay platform fees with MTRX (50% discount)</li>
              <li>• <strong>Governance:</strong> Vote on project listings and platform upgrades</li>
              <li>• <strong>Priority Access:</strong> Higher staking = earlier project access</li>
            </ul>

            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>4.2 Token Distribution</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-brand-primary mb-1">30%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Public Sale</div>
              </div>
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-brand-primary mb-1">25%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Team & Advisors (4yr vest)</div>
              </div>
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-brand-primary mb-1">20%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Ecosystem Development</div>
              </div>
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-brand-primary mb-1">15%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Marketing & Partnerships</div>
              </div>
            </div>

            <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>4.3 Revenue Model</h3>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li>• 2% fee on successful token raises</li>
              <li>• Project listing fees ($10K-$50K based on raise size)</li>
              <li>• Premium features subscription ($99-$499/month)</li>
              <li>• 50% of fees used for MTRX buyback & burn</li>
            </ul>
          </CardContent>
        </Card>

        {/* Roadmap */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>5. Roadmap</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-brand-primary pl-6">
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Q1 2026 - Platform Launch</h3>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>✓ MVP launch with Ethereum support</li>
                  <li>✓ First 5 project launches</li>
                  <li>✓ KYC integration with Onfido</li>
                  <li>✓ Security audit by CertiK</li>
                </ul>
              </div>

              <div className="border-l-4 border-brand-primary pl-6">
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Q2 2026 - Multi-Chain Expansion</h3>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• BSC and Polygon integration</li>
                  <li>• Launch MTRX governance token</li>
                  <li>• Mobile app (iOS/Android)</li>
                  <li>• Staking mechanism activation</li>
                </ul>
              </div>

              <div className="border-l-4 border-brand-primary pl-6">
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Q3 2026 - Advanced Features</h3>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• NFT launchpad integration</li>
                  <li>• Liquidity pool creation tools</li>
                  <li>• DAO governance framework</li>
                  <li>• API for third-party integrations</li>
                </ul>
              </div>

              <div className="border-l-4 border-brand-primary pl-6">
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Q4 2026 - Ecosystem Growth</h3>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Cross-chain bridge partnerships</li>
                  <li>• Institutional investor onboarding</li>
                  <li>• Launch accelerator program</li>
                  <li>• Global marketing campaigns</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team */}
        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>6. Team & Advisors</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Our team combines 50+ years of experience in blockchain development, fintech, and venture capital:
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li>• Former engineers from Coinbase, Binance, Uniswap</li>
              <li>• Ex-VCs from Andreessen Horowitz, Paradigm</li>
              <li>• Legal counsel from top crypto law firms</li>
              <li>• Marketing veterans from successful Web3 projects</li>
            </ul>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>7. Conclusion</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              MatrixLaunch is positioned to become the leading decentralized launchpad by prioritizing investor protection, project quality, and sustainable community growth. Our multi-chain approach, combined with innovative tokenomics and rigorous vetting, creates a platform where both projects and investors can succeed long-term.
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              As the crypto market matures and regulatory frameworks solidify, platforms like MatrixLaunch that emphasize compliance, transparency, and fairness will emerge as industry standards. We invite you to join us in building the future of blockchain fundraising.
            </p>
          </CardContent>
        </Card>

        {/* Download CTA */}
        <div className="mt-12 text-center">
          <p className="mb-4" style={{ color: 'var(--text-muted)' }}>Download full whitepaper (PDF)</p>
          <button className="btn-primary">
            <FileText size={18} />
            Download Whitepaper (Coming Soon)
          </button>
        </div>
      </div>
    </div>
  );
};

export default Whitepaper;
